import React, { useState } from "react";
import { supabase } from '../../supabaseClient';
import { sendApplicationNotification } from '../../utils/applicationNotifications';
import './CulinaryCareerPortal.css'

export default function CulinaryCareerPortal() {
  const [activeCategory, setActiveCategory] = useState("all");
  const [showModal, setShowModal] = useState(false);
  const [selectedPosition, setSelectedPosition] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    position: '',
    experience: '',
    message: '',
    resume: null
  });
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('');
  const openModal = (position) => {
    setSelectedPosition(position);
    setFormData(prev => ({ ...prev, position }));
    setShowModal(true);
    setSubmitStatus('');
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedPosition('');
    setFormData({
      name: '',
      email: '',
      phone: '',
      position: '',
      experience: '',
      message: '',
      resume: null
    });
    setLoading(false);
    setUploading(false);
    setSubmitStatus('');
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    setFormData(prev => ({ ...prev, resume: e.target.files[0] }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSubmitStatus('submitting');
    
    console.log('📝 Starting application submission...');
    
    try {
      let resumeUrl = null;
      
      // Upload resume if provided
      if (formData.resume) {
        setUploading(true);
        console.log('📄 Uploading resume...');
        
        if (formData.resume.size > 5 * 1024 * 1024) {
          alert('File size too large. Please upload a file smaller than 5MB.');
          setLoading(false);
          setUploading(false);
          setSubmitStatus('error');
          return;
        }

        const fileExt = formData.resume.name.split('.').pop();
        const fileName = `${Math.random()}-${Date.now()}.${fileExt}`;
        
        const { error: uploadError } = await supabase.storage
          .from('resumes')
          .upload(fileName, formData.resume);
        
        if (uploadError) {
          console.error('Upload error:', uploadError);
          throw new Error(`File upload failed: ${uploadError.message}`);
        }
        
        const { data: urlData } = supabase.storage
          .from('resumes')
          .getPublicUrl(fileName);
        
        resumeUrl = urlData.publicUrl;
        console.log('✅ Resume uploaded. URL:', resumeUrl);
        setUploading(false);
      }

      // Save to database
      console.log('💾 Saving to database...');
      const { data: application, error } = await supabase
        .from('applications')
        .insert({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          position: formData.position || selectedPosition,
          experience: formData.experience,
          message: formData.message,
          resume_url: resumeUrl,
          status: 'new',
          created_at: new Date().toISOString()
        })
        .select()
        .single();
      
      if (error) {
        console.error('Database error:', error);
        throw new Error(`Database operation failed: ${error.message}`);
      }
      
      console.log('✅ Database save successful:', application);

      console.log('📧 Triggering application notification email...');
      const notificationResult = await sendApplicationNotification({
        applicationId: application?.id,
        applicantName: application?.name ?? formData.name,
        applicantEmail: application?.email ?? formData.email,
        positionName:
          application?.position ?? formData.position ?? selectedPosition,
        resumeUrl: application?.resume_url ?? resumeUrl,
        phone: application?.phone ?? formData.phone,
        experience: application?.experience ?? formData.experience,
        message: application?.message ?? formData.message,
      });

      if (!notificationResult || notificationResult.success === false) {
        console.warn('⚠️ Application email notification failed.', {
          notificationResult,
        });
      } else {
        console.log('✅ Application email notification sent.', {
          notificationResult,
        });
      }

      setSubmitStatus('success');
      console.log('🎉 Application submitted successfully!');
      
      setTimeout(() => {
        closeModal();
      }, 3000);
      
    } catch (error) {
      console.error('❌ Error details:', error);
      setSubmitStatus('error');
      alert(`There was an error submitting your application: ${error.message}. Please try again or contact us directly.`);
    } finally {
      setLoading(false);
      setUploading(false);
    }
  };

  return (
    <div className="we-are-hiring-page">
      <section className="hiring-hero-section">
        <div className="hiring-container">
          <div className="hiring-hero-content">
            
            {/* Header */}
            <div className="hiring-header">
              <div className="sparkle-badge">
                <div className="sparkle-container">
                  <div className="sparkle s1">✨</div>
                  <div className="sparkle s2">✨</div>
                  <div className="sparkle s3">✨</div>
                </div>
                <div className="badge-content">
                  <span className="badge-text">Michelin-Star Team • Hiring Excellence</span>
                </div>
              </div>
            </div>

            {/* Enhanced CTA Section */}
            <div className="hiring-cta-section">
              <div className="cta-container">
                <button 
                  className="premium-cta-btn primary-3d"
                  onClick={() => openModal('Home Cook')}
                >
                  <div className="btn-bg-glow"></div>
                  <div className="btn-content">
                    <span className="btn-icon">
                      <i className="fas fa-file-upload"></i>
                    </span>
                    <span className="btn-text">Apply to Become a Culinary Master</span>
                    <span className="btn-arrow">
                      <i className="fas fa-arrow-right"></i>
                    </span>
                  </div>
                  <div className="btn-shine"></div>
                </button>
              </div>
            </div>

            {/* Enhanced Description */}
            <div className="hiring-description">
              <div className="description-wrapper">
                <p className="main-description">
                  Join the <span className="highlight-word">elite culinary team</span> that's redefining 
                  gourmet experiences. We're seeking passionate professionals who 
                  <span className="accent-word"> excel in innovation</span> and 
                  <span className="accent-word"> master craftsmanship</span>.
                </p>
                <div className="description-features">
                  <div className="feature-tag">
                    <i className="fas fa-award"></i>
                    <span>Michelin Standards</span>
                  </div>
                  <div className="feature-tag">
                    <i className="fas fa-rocket"></i>
                    <span>Rapid Growth</span>
                  </div>
                  <div className="feature-tag">
                    <i className="fas fa-gem"></i>
                    <span>Premium Benefits</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Enhanced Stats */}
            <div className="hiring-stats">
              <div className="stats-grid">
                <div className="stat-item premium">
                  <div className="stat-icon-wrapper">
                    <div className="stat-icon">👑</div>
                    <div className="icon-glow"></div>
                  </div>
                  <div className="stat-content">
                    <div className="stat-number">50+</div>
                    <div className="stat-label">Master Chefs</div>
                  </div>
                </div>

                <div className="stat-item premium">
                  <div className="stat-icon-wrapper">
                    <div className="stat-icon">🌍</div>
                    <div className="icon-glow"></div>
                  </div>
                  <div className="stat-content">
                    <div className="stat-number">3+</div>
                    <div className="stat-label">Global Locations</div>
                  </div>
                </div>

                <div className="stat-item premium">
                  <div className="stat-icon-wrapper">
                    <div className="stat-icon">💫</div>
                    <div className="icon-glow"></div>
                  </div>
                  <div className="stat-content">
                    <div className="stat-number">98%</div>
                    <div className="stat-label">Success Rate</div>
                  </div>
                </div>

                <div className="stat-item premium">
                  <div className="stat-icon-wrapper">
                    <div className="stat-icon">⚡</div>
                    <div className="icon-glow"></div>
                  </div>
                  <div className="stat-content">
                    <div className="stat-number">4.3</div>
                    <div className="stat-label">Star Rating</div>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Apply Now Modal - Same as before */}
      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-container" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Apply for {selectedPosition}</h3>
              <button 
                className="modal-close" 
                onClick={closeModal} 
                disabled={loading}
                aria-label="Close modal"
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            
            {submitStatus === 'success' ? (
              <div className="submit-success">
                <div className="success-icon">
                  <i className="fas fa-check-circle"></i>
                </div>
                <h4>Application Submitted Successfully!</h4>
                <p>Thank you for applying to the position. We'll review your application and get back to you soon.</p>
                <button onClick={closeModal} className="btn btn-primary">
                  Close
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="application-form">
                <div className="form-group">
                  <label htmlFor="name">Full Name *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    disabled={loading}
                    placeholder="Enter your full name"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="email">Email Address *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    disabled={loading}
                    placeholder="Enter your email address"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="phone">Phone Number</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    disabled={loading}
                    placeholder="Enter your phone number"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="experience">Years of Experience</label>
                  <select
                    id="experience"
                    name="experience"
                    value={formData.experience}
                    onChange={handleInputChange}
                    disabled={loading}
                  >
                    <option value="">Select experience level</option>
                    <option value="0-1">0-1 years</option>
                    <option value="1-3">1-3 years</option>
                    <option value="3-5">3-5 years</option>
                    <option value="5+">5+ years</option>
                  </select>
                </div>
                
                <div className="form-group">
                  <label htmlFor="message">Why are you interested in this position?</label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    rows="4"
                    disabled={loading}
                    placeholder="Tell us why you're interested in this position and what makes you a good fit..."
                  ></textarea>
                </div>
                
                <div className="form-group">
                  <label htmlFor="resume">Upload Resume (PDF, DOC, DOCX) *</label>
                  <input
                    type="file"
                    id="resume"
                    name="resume"
                    onChange={handleFileChange}
                    accept=".pdf,.doc,.docx"
                    required
                    disabled={loading}
                  />
                  <small>Max file size: 5MB. Accepted formats: PDF, DOC, DOCX</small>
                  {formData.resume && (
                    <div className="file-selected">
                      <i className="fas fa-check"></i> Selected: {formData.resume.name}
                    </div>
                  )}
                </div>
                
                <div className="form-actions">
                  <button 
                    type="button" 
                    onClick={closeModal} 
                    className="btn btn-outline" 
                    disabled={loading}
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit" 
                    className="btn btn-primary" 
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <i className="fas fa-spinner fa-spin"></i>
                        {uploading ? 'Uploading Resume...' : 'Submitting Application...'}
                      </>
                    ) : (
                      'Submit Application'
                    )}
                  </button>
                </div>
                
                {submitStatus === 'error' && (
                  <div className="error-message">
                    <i className="fas fa-exclamation-triangle"></i>
                    There was an error submitting your application. Please try again.
                  </div>
                )}
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
